/**
 * isIncompleteInterval -- Returns true if the last time-period has not been completed.
 * NOTE: This function assumes that the date range for the data is relative to the current time
 * (e.g. last 30 days). It doesn't work for fixed date ranges. For example, if the date range is from
 * [2020-01-15, 2020-03-15] and the unit is month, and it's currently 2020-09-01, this function would
 * incorrectly return false even though [2020-03-01, 2020-03-15] is an incomplete month.
 *
 * @param {Hash} series A single series, in highcharts format
 * @param {Hash} an options hash.
 *      * (required) 'unit' interval in milliseconds
 *        OR A string describing the interval ('hour', 'day', 'week', 'month')
 *      * (required) 'utcOffset' timezone offset in minutes
 *        OR A string describing the interval ('hour', 'day', 'week', 'month')
 *      * "adjustForLocalTime" This adjusts the estimation for local time.
 * @return {Boolean} true/false
 */
export function isIncompleteInterval(data, options) {
  options = options || {};
  const unit = options.unit;
  if (data && data.length > 0 && unit) {
    let timeInterval;
    const lastPoint = data[data.length - 1];
    const lastDate = Array.isArray(lastPoint) ? lastPoint[0] : lastPoint.x;
    const now = new Date();
    let currentTime = now.getTime();
    let timezoneOffset = options.utcOffset || 0;
    if (options.adjustForLocalTime) {
      // many dates passed in are in the local time of the browser.
      timezoneOffset += now.getTimezoneOffset();
    }
    currentTime = currentTime + 60000 * timezoneOffset;

    const currentInterval = currentTime - lastDate;
    if (Number.isInteger(unit)) {
      timeInterval = unit;
    } else {
      const msInHour = 60 * 60 * 1000;
      const date = new Date(currentTime); // change date to account for offset
      switch (unit.toLowerCase()) {
        case `minute`:
          // Minute intervals not shown on dashboards
          // instead we default to an hour
          timeInterval = msInHour;
          break;
        case `hour`:
          timeInterval = msInHour;
          break;
        case `day`:
          timeInterval = 24 * msInHour;
          break;
        case `week`:
          timeInterval = 7 * 24 * msInHour;
          break;
        case `month`: {
          const start = new Date(date.getYear(), date.getMonth());
          const end = new Date(date.getYear(), date.getMonth() + 1);
          timeInterval = end.getTime() - start.getTime();
          break;
        }
        case `quarter`: {
          const last = new Date(lastDate);
          const nowMonths = date.getUTCFullYear() * 12 + date.getUTCMonth();
          const lastMonths = last.getUTCFullYear() * 12 + last.getUTCMonth();
          return nowMonths - lastMonths < 3;
        }
        default:
          console.error(`Unknown interval: "${timeInterval}"`);
          return false;
      }
    }

    return currentInterval < timeInterval;
  }

  return false;
}
